import {
  ComponentType,
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useProject } from 'store/project';
import { singleIssueReportOnViewToModel } from 'shared/domain/report/mapping/toModel';
import { ReportType } from 'shared/domain/report/types/model/common';
import { SingleIssueReportModel } from 'shared/domain/report/types/model/singleIssueReportModel';
import { CreateReportDialog } from 'views/reports/wizard/dialog';
import { useCurrentIssueParams } from '../../../dataProviders/withIssue';
import { ContentSelection } from '../contentSelection';
import { ReportAvailabilityShell } from '../contexts/ReportAvailabilityShell';
import { ReportFormEstimateShell } from '../contexts/ReportPageEstimateContext';
import { useReportDialogBase } from '../hooks/useReportDialogBase';
import {
  validateIssueReport,
  handleIssueReportFormValidation,
} from '../reportValidation';
import {
  useIssueReportInitialValues,
  saveIssueReportValues,
} from '../hooks/useReportInitialValues';
import {
  CreateSingleIssueReportContext,
  SingleIssueReportOnView,
} from './types';

const createSingleIssueReportContext = createContext<
  CreateSingleIssueReportContext | undefined
>(undefined);

const WithCreateSingleIssueReport: FC<{ children?: ReactNode }> = ({
  children,
}) => {

  const {
    open,
    isPosting,
    openDialog,
    closeDialog,
    releaseSubmitEvent,
    submitReport,
    SUBMIT_EVENT_TYPE,
  } = useReportDialogBase<SingleIssueReportModel>(
    'submit-single-issue-report'
  );

  const issueParams = useCurrentIssueParams();
  const { issueId, processId } = issueParams;

  const project = useProject();

  const submitForm = useCallback(
    async (values: SingleIssueReportOnView) => {
      const reportModel = singleIssueReportOnViewToModel({
        ...values,
        issueId: issueId,
      });

      return submitReport({
        reportModel,
        onBeforeSubmit: () => {
          saveIssueReportValues(
            ReportType.singleIssue,
            project._id,
            values,
            processId
          );
        },
      });
    },
    [issueId, processId, project._id, submitReport]
  );

  const baseInitialValues = useIssueReportInitialValues(
    ReportType.singleIssue,
    processId,
    [open, issueId]
  );

  const initialValues: SingleIssueReportOnView = useMemo(
    () => ({
      ...baseInitialValues,
      issueId: '',
    }),
    [baseInitialValues]
  );

  const contentSelection = useCallback(
    () => <ContentSelection reportType={ReportType.singleIssue} />,
    []
  );

  const wrapForm = useCallback(
    (form: ReactNode) => (
      <ReportAvailabilityShell reportType={ReportType.singleIssue} open={open}>
        <ReportFormEstimateShell open={open} kind={ReportType.singleIssue}>
          {form}
        </ReportFormEstimateShell>
      </ReportAvailabilityShell>
    ),
    [open]
  );

  const ctx: CreateSingleIssueReportContext = useMemo(() => {
    return {
      open,
      openDialog,
      closeDialog,
      submitForm,
      isPosting,
      initialValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateIssueReport,
      handleFormValidation: handleIssueReportFormValidation,
      ContentSelection: contentSelection,
      issueParams,
      reportEstimateKind: ReportType.singleIssue,
      wrapForm,
    };
  }, [
    open,
    openDialog,
    closeDialog,
    submitForm,
    isPosting,
    initialValues,
    SUBMIT_EVENT_TYPE,
    releaseSubmitEvent,
    contentSelection,
    issueParams,
    wrapForm,
  ]);

  return (
    <createSingleIssueReportContext.Provider value={ctx}>
      {children}
      <CreateReportDialog
        useReportContext={useCreateSingleIssueReport}
        titleId="issue_list_send_report_action"
      />
    </createSingleIssueReportContext.Provider>
  );
};

function useCreateSingleIssueReport(): CreateSingleIssueReportContext {
  const context = useContext(createSingleIssueReportContext);
  if (context === undefined) {
    throw new Error(
      'useCreateSingleIssueReport must be used within an CreateSingleIssueReportContext'
    );
  }
  return context;
}

const withCreateSingleIssueReport =
  (Component: ComponentType) =>
  ({ ...props }): ReactElement => (
    <WithCreateSingleIssueReport>
      <Component {...props} />
    </WithCreateSingleIssueReport>
  );

export {
  useCreateSingleIssueReport,
  withCreateSingleIssueReport,
  WithCreateSingleIssueReport,
};
